import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE } from '../apiConfig';
import './EventApply.css';

const EventApply = () => {
  const { eventId, formId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [event, setEvent] = useState(null);
  const [form, setForm] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    fetch(`${API_BASE}/event-form/${eventId}/${formId}`)
      .then(res => {
        if (!res.ok) throw new Error('Form not found');
        return res.json();
      })
      .then(data => {
        setEvent(data.event);

        // Parse pages format
        let fieldsData = data.form.fields || [];
        if (typeof fieldsData === 'string') {
          try { fieldsData = JSON.parse(fieldsData); } catch (e) { fieldsData = []; }
        }

        let pages;
        if (fieldsData.length > 0 && fieldsData[0].pageTitle !== undefined) {
          // New pages format
          pages = fieldsData;
        } else {
          // Old flat format � wrap in single page
          pages = [{ pageTitle: 'Application', fields: fieldsData }];
        }

        // Store pages in form object
        const formObj = { ...data.form, pages };
        setForm(formObj);

        // Initialize values from all fields across all pages
        const initialValues = {};
        pages.forEach(page => {
          (page.fields || []).forEach(f => {
            if (['experience', 'certificate', 'course'].includes(f.type)) {
              initialValues[f.name] = [];
            } else if (f.type === 'checkbox') {
              initialValues[f.name] = [];
            } else if (f.type === 'file') {
              initialValues[f.name] = null;
            } else {
              initialValues[f.name] = '';
            }
          });
        });
        setFormValues(initialValues);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [eventId, formId]);

  const validateField = useCallback((field, value) => {
    const errors = [];
    const isCompound = ['experience', 'certificate', 'course'].includes(field.type);
    const isCheckbox = field.type === 'checkbox';
    const isFile = field.type === 'file';

    if (field.required) {
      if (isCompound && (!value || value.length === 0)) {
        errors.push(`${field.label} is required � add at least one entry`);
        return errors;
      }
      if (isCheckbox && (!value || value.length === 0)) {
        errors.push(`${field.label} is required � select at least one option`);
        return errors;
      }
      if (isFile && !value) {
        errors.push(`${field.label} is required`);
        return errors;
      }
      if (!isCompound && !isCheckbox && !isFile && (!value || value.toString().trim() === '')) {
        errors.push(`${field.label} is required`);
        return errors;
      }
    }

    if (isCompound || isCheckbox || isFile) return errors;
    if (!value || value.toString().trim() === '') return errors;

    // Validation rules
    const validation = field.validation || 'none';
    switch (validation) {
      case 'numeric':
        if (!/^\d+(\.\d+)?$/.test(value)) errors.push(`${field.label} must be a number`);
        break;
      case 'alpha':
        if (!/^[\p{L}\s]+$/u.test(value)) errors.push(`${field.label} must contain only letters`);
        break;
      case 'alpha_en':
        if (!/^[a-zA-Z\s]+$/.test(value)) errors.push(`${field.label} must be in English letters only`);
        break;
      case 'alpha_ar':
        if (!/^[\u0600-\u06FF\s]+$/.test(value)) errors.push(`${field.label} must be in Arabic letters only`);
        break;
      case 'phone':
        if (!/^[+]?[0-9\s\-()]{7,20}$/.test(value)) errors.push(`${field.label} must be a valid phone number`);
        break;
      case 'website':
        try { new URL(value); } catch { errors.push(`${field.label} must be a valid URL`); }
        break;
      default:
        break;
    }

    if (field.type === 'email' && value) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        errors.push(`${field.label} must be a valid email`);
      }
    }

    // Character limits
    if (field.maxLength && value.length > parseInt(field.maxLength)) {
      errors.push(`${field.label} must be at most ${field.maxLength} characters`);
    }
    if (field.minLength && value.length < parseInt(field.minLength)) {
      errors.push(`${field.label} must be at least ${field.minLength} characters`);
    }

    // Number range
    if (field.type === 'number' && value) {
      const num = parseFloat(value);
      if (field.min !== undefined && field.min !== '' && num < parseFloat(field.min)) {
        errors.push(`${field.label} must be at least ${field.min}`);
      }
      if (field.max !== undefined && field.max !== '' && num > parseFloat(field.max)) {
        errors.push(`${field.label} must be at most ${field.max}`);
      }
    }

    return errors;
  }, []);

  const handleChange = (field, value) => {
    setFormValues(prev => ({ ...prev, [field.name]: value }));
    const errs = validateField(field, value);
    setFieldErrors(prev => ({ ...prev, [field.name]: errs }));
  };

  const validateCurrentStep = () => {
    if (!form || !form.pages) return false;
    const page = form.pages[currentStep];
    if (!page) return false;
    let allValid = true;
    const newErrors = { ...fieldErrors };
    page.fields.forEach(field => {
      const errs = validateField(field, formValues[field.name]);
      newErrors[field.name] = errs;
      if (errs.length > 0) allValid = false;
    });
    setFieldErrors(newErrors);
    return allValid;
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      if (currentStep < form.pages.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    let allValid = true;
    const newErrors = {};
    // Validate all fields across all pages
    form.pages.forEach((page, pageIdx) => {
      page.fields.forEach(field => {
        const errs = validateField(field, formValues[field.name]);
        if (errs.length > 0) {
          allValid = false;
          newErrors[field.name] = errs;
        }
      });
    });
    setFieldErrors(newErrors);

    if (!allValid) {
      // Jump to first page with errors
      const firstErrorPage = form.pages.findIndex(page =>
        page.fields.some(f => newErrors[f.name]?.length > 0)
      );
      if (firstErrorPage >= 0) setCurrentStep(firstErrorPage);
      return;
    }

    setSubmitting(true);
    setServerErrors([]);

    try {
      const submitData = {};
      form.pages.forEach(page => {
        page.fields.forEach(field => {
          const val = formValues[field.name];
          if (['experience', 'certificate', 'course'].includes(field.type)) {
            submitData[field.name] = JSON.stringify(val);
          } else if (field.type === 'checkbox') {
            submitData[field.name] = val.join(', ');
          } else if (field.type === 'file') {
            submitData[field.name] = val ? val.name : '';
          } else {
            submitData[field.name] = val;
          }
        });
      });

      const res = await fetch(`${API_BASE}/event-form/${eventId}/${formId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      const data = await res.json();

      if (data.success) {
        setSubmitted(true);
      } else if (data.errors) {
        setServerErrors(data.errors);
      } else {
        setServerErrors([data.error || 'Submission failed']);
      }
    } catch (err) {
      setServerErrors(['Network error. Please try again.']);
    }
    setSubmitting(false);
  };

  // ===== COMPOUND FIELD HANDLERS =====
  const addCompoundItem = (fieldName, type) => {
    const templates = {
      experience: { title: '', company: '', start_date: '', end_date: '', description: '' },
      certificate: { name: '', issuer: '', date: '', description: '' },
      course: { name: '', provider: '', date: '', description: '' },
    };
    setFormValues(prev => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), { ...templates[type] }],
    }));
  };

  const updateCompoundItem = (fieldName, index, key, value) => {
    setFormValues(prev => {
      const items = [...(prev[fieldName] || [])];
      items[index] = { ...items[index], [key]: value };
      return { ...prev, [fieldName]: items };
    });
  };

  const removeCompoundItem = (fieldName, index) => {
    setFormValues(prev => {
      const items = [...(prev[fieldName] || [])];
      items.splice(index, 1);
      return { ...prev, [fieldName]: items };
    });
  };

  // ===== RENDER FIELD =====
  const renderField = (field, index) => {
    // If the field is a position field and we have jobs_allowed, render as dropdown
    const isPositionField = field.name === 'position' && form.jobs_allowed && form.jobs_allowed.length > 0;
    if (isPositionField) {
      field = { ...field, type: 'select', options: form.jobs_allowed.join(', ') };
    }
    const value = formValues[field.name];
    const errs = fieldErrors[field.name] || [];
    const hasError = errs.length > 0;
    const isSimple = !['experience', 'certificate', 'course', 'checkbox', 'radio', 'file'].includes(field.type);
    const isValid = isSimple ? (value && !hasError) : (!hasError && value && (Array.isArray(value) ? value.length > 0 : !!value));

    let input;

    switch (field.type) {
      case 'textarea':
        input = (
          <div>
            <textarea
              className={`ea-input ${hasError ? 'ea-input-error' : ''} ${isValid ? 'ea-input-valid' : ''}`}
              value={value || ''}
              onChange={e => handleChange(field, e.target.value)}
              placeholder={`Enter ${field.label}...`}
              rows={4}
              maxLength={field.maxLength || undefined}
            />
            {field.maxLength && (
              <div className={`ea-char-count ${(value || '').length > field.maxLength * 0.9 ? 'ea-char-warn' : ''} ${(value || '').length > field.maxLength ? 'ea-char-over' : ''}`}>
                {(value || '').length}/{field.maxLength}
              </div>
            )}
          </div>
        );
        break;

      case 'select':
        const selectOptions = (field.options || '').split(',').map(o => o.trim()).filter(Boolean);
        input = (
          <select
            className={`ea-input ${hasError ? 'ea-input-error' : ''} ${isValid ? 'ea-input-valid' : ''}`}
            value={value || ''}
            onChange={e => handleChange(field, e.target.value)}
          >
            <option value="">-- Select {field.label} --</option>
            {selectOptions.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );
        break;

      case 'radio':
        const radioOptions = (field.options || '').split(',').map(o => o.trim()).filter(Boolean);
        input = (
          <div className="ea-options-group">
            {radioOptions.map((opt, i) => (
              <label
                key={i}
                className={`ea-option-item ${value === opt ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name={field.name}
                  value={opt}
                  checked={value === opt}
                  onChange={e => handleChange(field, e.target.value)}
                />
                <span className="ea-option-label">{opt}</span>
              </label>
            ))}
          </div>
        );
        break;

      case 'checkbox':
        const checkOptions = (field.options || '').split(',').map(o => o.trim()).filter(Boolean);
        const checkedValues = value || [];
        input = (
          <div className="ea-options-group">
            {checkOptions.map((opt, i) => (
              <label
                key={i}
                className={`ea-option-item ${checkedValues.includes(opt) ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  value={opt}
                  checked={checkedValues.includes(opt)}
                  onChange={e => {
                    const newVals = e.target.checked
                      ? [...checkedValues, opt]
                      : checkedValues.filter(v => v !== opt);
                    handleChange(field, newVals);
                  }}
                />
                <span className="ea-option-label">{opt}</span>
              </label>
            ))}
          </div>
        );
        break;

      case 'nationality':
        input = <NationalityField field={field} value={value} onChange={handleChange} hasError={hasError} isValid={isValid} />;
        break;

      case 'file':
        input = <FileUploadField field={field} value={value} onChange={handleChange} hasError={hasError} isValid={isValid} />;
        break;

      case 'experience':
        input = (
          <CompoundField
            field={field}
            items={value || []}
            type="experience"
            labels={{ title: 'Job Title', company: 'Company', start_date: 'Start Date', end_date: 'End Date', description: 'Job Description' }}
            onAdd={() => addCompoundItem(field.name, 'experience')}
            onUpdate={(idx, key, val) => updateCompoundItem(field.name, idx, key, val)}
            onRemove={(idx) => removeCompoundItem(field.name, idx)}
          />
        );
        break;

      case 'certificate':
        input = (
          <CompoundField
            field={field}
            items={value || []}
            type="certificate"
            labels={{ name: 'Certificate Name', issuer: 'Issued By', date: 'Date Obtained', description: 'Description' }}
            onAdd={() => addCompoundItem(field.name, 'certificate')}
            onUpdate={(idx, key, val) => updateCompoundItem(field.name, idx, key, val)}
            onRemove={(idx) => removeCompoundItem(field.name, idx)}
          />
        );
        break;

      case 'course':
        input = (
          <CompoundField
            field={field}
            items={value || []}
            type="course"
            labels={{ name: 'Course Name', provider: 'Provider', date: 'Date', description: 'Description' }}
            onAdd={() => addCompoundItem(field.name, 'course')}
            onUpdate={(idx, key, val) => updateCompoundItem(field.name, idx, key, val)}
            onRemove={(idx) => removeCompoundItem(field.name, idx)}
          />
        );
        break;

      case 'date':
        input = (
          <input
            type="date"
            className={`ea-input ${hasError ? 'ea-input-error' : ''} ${isValid ? 'ea-input-valid' : ''}`}
            value={value || ''}
            onChange={e => handleChange(field, e.target.value)}
          />
        );
        break;

      case 'number':
        input = (
          <input
            type="number"
            className={`ea-input ${hasError ? 'ea-input-error' : ''} ${isValid ? 'ea-input-valid' : ''}`}
            value={value || ''}
            onChange={e => handleChange(field, e.target.value)}
            placeholder={`Enter ${field.label}...`}
            min={field.min}
            max={field.max}
          />
        );
        break;

      case 'email':
        input = (
          <input
            type="email"
            className={`ea-input ${hasError ? 'ea-input-error' : ''} ${isValid ? 'ea-input-valid' : ''}`}
            value={value || ''}
            onChange={e => handleChange(field, e.target.value)}
            placeholder={`Enter ${field.label}...`}
          />
        );
        break;

      case 'tel':
      case 'phone':
        input = (
          <input
            type="tel"
            className={`ea-input ${hasError ? 'ea-input-error' : ''} ${isValid ? 'ea-input-valid' : ''}`}
            value={value || ''}
            onChange={e => handleChange(field, e.target.value)}
            placeholder={`e.g. +20 1XX XXX XXXX`}
          />
        );
        break;

      default: // text
        input = (
          <div>
            <input
              type="text"
              className={`ea-input ${hasError ? 'ea-input-error' : ''} ${isValid ? 'ea-input-valid' : ''}`}
              value={value || ''}
              onChange={e => handleChange(field, e.target.value)}
              placeholder={`Enter ${field.label}...`}
              maxLength={field.maxLength || undefined}
            />
            {field.maxLength && (
              <div className={`ea-char-count ${(value || '').length > field.maxLength * 0.9 ? 'ea-char-warn' : ''}`}>
                {(value || '').length}/{field.maxLength}
              </div>
            )}
          </div>
        );
    }

    const validationHints = {
      numeric: 'Numbers only',
      alpha: 'Letters only',
      alpha_en: 'English letters only',
      alpha_ar: 'Arabic letters only',
      phone: 'Phone number format',
      website: 'URL format (https://...)',
    };

    return (
      <div className="ea-field-container" key={index}>
        <label className="ea-label">
          {field.label}
          {field.required && <span className="ea-required">*</span>}
        </label>
        {field.validation && validationHints[field.validation] && (
          <div className="ea-validation-hint">
            {validationHints[field.validation]}
          </div>
        )}
        {input}
        {hasError && (
          <div className="ea-error-msg">
            {errs.map((e, i) => <span key={i}>{e}</span>)}
          </div>
        )}
        {isValid && !['experience', 'certificate', 'course'].includes(field.type) && (
          <div className="ea-success-msg">
            <i className="fas fa-check-circle"></i> Looks good!
          </div>
        )}
      </div>
    );
  };

  // ===== RENDER STATES =====
  if (loading) {
    return (
      <div className="ea-loading-container">
        <div className="ea-spinner"></div>
        <p>Loading application form...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ea-error-container">
        <div className="ea-error-icon"><i className="fas fa-exclamation-triangle"></i></div>
        <h2>Form Not Available</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="ea-success-container">
        <div className="ea-success-icon"><i className="fas fa-check-circle"></i></div>
        <h2>Application Submitted!</h2>
        <p>Thank you for applying to <strong>{event.name}</strong>.</p>
        <p>We will review your application and get back to you soon.</p>
      </div>
    );
  }

  const totalSteps = form.pages.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const currentPage = form.pages[currentStep];
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className="ea-page">
      <div className="ea-container">
        {/* Header */}
        <div className="ea-header">
          <div className="ea-event-badge">{event.name}</div>
          <h1 className="ea-title">{form.title}</h1>
          {form.description && <p className="ea-desc">{form.description}</p>}
          <div className="ea-event-info">
            <span><i className="fas fa-map-marker-alt"></i> {event.location}</span>
            <span><i className="fas fa-calendar-alt"></i> {event.start_date} � {event.end_date}</span>
          </div>
        </div>

        {/* Progress */}
        <div className="ea-progress-section">
          <div className="ea-progress-bar">
            <div className="ea-progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="ea-progress-text">
            {currentPage.pageTitle && <strong>{currentPage.pageTitle}</strong>}
            {' � '}Step {currentStep + 1} of {totalSteps}
          </div>
        </div>

        {/* Page Fields */}
        <div className="ea-step-card" key={currentStep}>
          {currentPage.pageTitle && (
            <h2 style={{
              fontSize: '1.15rem', fontWeight: 700, color: '#1e293b',
              marginBottom: 18, paddingBottom: 12,
              borderBottom: '2px solid #f1f5f9'
            }}>
              {currentPage.pageTitle}
            </h2>
          )}
          {currentPage.fields.map((field, index) => renderField(field, index))}
        </div>

        {/* Server Errors */}
        {serverErrors.length > 0 && (
          <div className="ea-server-errors">
            {serverErrors.map((e, i) => <p key={i}>{e}</p>)}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="ea-nav-buttons">
          <button
            className="ea-btn ea-btn-prev"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            <i className="fas fa-arrow-left"></i> Previous
          </button>

          {isLastStep ? (
            <button
              className="ea-btn ea-btn-submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <><span className="ea-btn-spinner"></span> Submitting...</>
              ) : (
                <><i className="fas fa-paper-plane"></i> Submit Application</>
              )}
            </button>
          ) : (
            <button className="ea-btn ea-btn-next" onClick={nextStep}>
              Next <i className="fas fa-arrow-right"></i>
            </button>
          )}
        </div>

        {/* Step Dots � based on pages */}
        <div className="ea-step-dots">
          {form.pages.map((page, i) => {
            // Check if all fields in this page are valid
            const pageHasErrors = page.fields.some(f => (fieldErrors[f.name] || []).length > 0);
            const pageComplete = page.fields.every(f => {
              const val = formValues[f.name];
              return val && (Array.isArray(val) ? val.length > 0 : val);
            });
            let dotClass = 'ea-dot';
            if (i === currentStep) dotClass += ' ea-dot-active';
            else if (pageComplete && !pageHasErrors) dotClass += ' ea-dot-valid';
            else if (pageHasErrors) dotClass += ' ea-dot-error';
            return (
              <span
                key={i}
                className={dotClass}
                onClick={() => setCurrentStep(i)}
                title={page.pageTitle || `Page ${i + 1}`}
              ></span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ===== NATIONALITY FIELD COMPONENT =====
const NATIONALITIES = [
  'Afghan', 'Albanian', 'Algerian', 'American', 'Andorran', 'Angolan', 'Antiguan', 'Argentine', 'Armenian', 'Australian',
  'Austrian', 'Azerbaijani', 'Bahamian', 'Bahraini', 'Bangladeshi', 'Barbadian', 'Belarusian', 'Belgian', 'Belizean', 'Beninese',
  'Bhutanese', 'Bolivian', 'Bosnian', 'Botswanan', 'Brazilian', 'Bruneian', 'Bulgarian', 'Burkinabe', 'Burmese', 'Burundian',
  'Cambodian', 'Cameroonian', 'Canadian', 'Cape Verdean', 'Central African', 'Chadian', 'Chilean', 'Chinese', 'Colombian', 'Comorian',
  'Congolese', 'Costa Rican', 'Croatian', 'Cuban', 'Cypriot', 'Czech', 'Danish', 'Djiboutian', 'Dominican', 'Dutch',
  'East Timorese', 'Ecuadorean', 'Egyptian', 'Emirati', 'Equatorial Guinean', 'Eritrean', 'Estonian', 'Ethiopian', 'Fijian', 'Filipino',
  'Finnish', 'French', 'Gabonese', 'Gambian', 'Georgian', 'German', 'Ghanaian', 'Greek', 'Grenadian', 'Guatemalan',
  'Guinean', 'Guyanese', 'Haitian', 'Honduran', 'Hungarian', 'Icelandic', 'Indian', 'Indonesian', 'Iranian', 'Iraqi',
  'Irish', 'Israeli', 'Italian', 'Ivorian', 'Jamaican', 'Japanese', 'Jordanian', 'Kazakh', 'Kenyan', 'Kiribati',
  'Kuwaiti', 'Kyrgyz', 'Laotian', 'Latvian', 'Lebanese', 'Liberian', 'Libyan', 'Lithuanian', 'Luxembourgish', 'Macedonian',
  'Malagasy', 'Malawian', 'Malaysian', 'Maldivian', 'Malian', 'Maltese', 'Marshallese', 'Mauritanian', 'Mauritian', 'Mexican',
  'Micronesian', 'Moldovan', 'Monacan', 'Mongolian', 'Montenegrin', 'Moroccan', 'Mozambican', 'Namibian', 'Nauruan', 'Nepalese',
  'New Zealander', 'Nicaraguan', 'Nigerian', 'Nigerien', 'North Korean', 'Norwegian', 'Omani', 'Pakistani', 'Palauan', 'Palestinian',
  'Panamanian', 'Papua New Guinean', 'Paraguayan', 'Peruvian', 'Polish', 'Portuguese', 'Qatari', 'Romanian', 'Russian', 'Rwandan',
  'Saint Lucian', 'Salvadoran', 'Samoan', 'Saudi', 'Senegalese', 'Serbian', 'Seychellois', 'Sierra Leonean', 'Singaporean', 'Slovak',
  'Slovenian', 'Solomon Islander', 'Somali', 'South African', 'South Korean', 'South Sudanese', 'Spanish', 'Sri Lankan', 'Sudanese', 'Surinamese',
  'Swazi', 'Swedish', 'Swiss', 'Syrian', 'Taiwanese', 'Tajik', 'Tanzanian', 'Thai', 'Togolese', 'Tongan',
  'Trinidadian', 'Tunisian', 'Turkish', 'Turkmen', 'Tuvaluan', 'Ugandan', 'Ukrainian', 'Uruguayan', 'Uzbek', 'Vanuatuan',
  'Venezuelan', 'Vietnamese', 'Yemeni', 'Zambian', 'Zimbabwean'
];

const NationalityField = ({ field, value, onChange, hasError, isValid }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = NATIONALITIES.filter(n =>
    n.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div
        className={`ea-input ${hasError ? 'ea-input-error' : ''} ${isValid ? 'ea-input-valid' : ''}`}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', userSelect: 'none', minHeight: 44,
        }}
        onClick={() => setOpen(!open)}
      >
        <span style={{ color: value ? '#1e293b' : '#94a3b8' }}>
          {value || `Select ${field.label}...`}
        </span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '0.75rem', color: '#94a3b8' }}></i>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
          boxShadow: '0 10px 40px rgba(0,0,0,0.12)', marginTop: 6,
          maxHeight: 280, display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search nationality..."
              autoFocus
              style={{
                width: '100%', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: '8px 12px', fontSize: '0.88rem', outline: 'none',
                background: '#f8fafc',
              }}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length > 0 ? filtered.map(nat => (
              <div
                key={nat}
                onClick={() => {
                  onChange(field, nat);
                  setOpen(false);
                  setSearch('');
                }}
                style={{
                  padding: '9px 16px', cursor: 'pointer', fontSize: '0.88rem',
                  background: value === nat ? '#eff6ff' : 'transparent',
                  color: value === nat ? '#2563eb' : '#374151',
                  fontWeight: value === nat ? 600 : 400,
                  borderBottom: '1px solid #f8fafc',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.target.style.background = value === nat ? '#dbeafe' : '#f1f5f9'}
                onMouseLeave={e => e.target.style.background = value === nat ? '#eff6ff' : 'transparent'}
              >
                {nat}
              </div>
            )) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                No nationality found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== FILE UPLOAD COMPONENT =====
const FileUploadField = ({ field, value, onChange, hasError, isValid }) => {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const maxSizeMB = parseInt(field.maxFileSize) || 10;
  const acceptTypes = field.accept || '.pdf,.doc,.docx,.jpg,.jpeg,.png';

  // Build human-readable types label
  const typesLabel = acceptTypes
    .replace(/\./g, '')
    .replace(/,/g, ', ')
    .toUpperCase();

  const handleFile = (file) => {
    if (file) {
      // Check file size
      if (file.size > maxSizeMB * 1024 * 1024) {
        alert(`File size must be less than ${maxSizeMB}MB`);
        return;
      }
      // Check file type
      if (acceptTypes) {
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        const allowed = acceptTypes.split(',').map(t => t.trim().toLowerCase());
        if (!allowed.includes(ext)) {
          alert(`File type not allowed. Accepted: ${typesLabel}`);
          return;
        }
      }
      onChange(field, file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <div>
      <div
        className={`ea-file-upload ${dragOver ? 'ea-file-dragover' : ''} ${value ? 'ea-file-has-file' : ''} ${hasError ? 'ea-input-error' : ''}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <div className="ea-file-upload-icon">
          {value ? <i className="fas fa-file-check"></i> : <i className="fas fa-cloud-upload-alt"></i>}
        </div>
        {!value ? (
          <>
            <div className="ea-file-upload-text">
              <strong>Click to upload</strong> or drag and drop
            </div>
            <div className="ea-file-upload-hint">
              {typesLabel} � Max {maxSizeMB}MB
            </div>
          </>
        ) : (
          <div className="ea-file-name">
            <i className="fas fa-file"></i>
            {value.name}
            <button
              className="ea-file-remove"
              onClick={(e) => { e.stopPropagation(); onChange(field, null); }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        className="ea-file-input-hidden"
        accept={acceptTypes}
        onChange={e => handleFile(e.target.files[0])}
      />
    </div>
  );
};

// ===== COMPOUND FIELD COMPONENT =====
const CompoundField = ({ field, items, type, labels, onAdd, onUpdate, onRemove }) => {
  const typeLabels = {
    experience: 'Experience',
    certificate: 'Certificate',
    course: 'Course',
  };

  const renderItemFields = (item, idx) => {
    const keys = Object.keys(labels);

    return (
      <div className="ea-compound-item" key={idx}>
        <div className="ea-compound-item-header">
          <span className="ea-compound-item-number">
            {typeLabels[type]} #{idx + 1}
          </span>
          <button
            className="ea-compound-remove"
            onClick={() => onRemove(idx)}
            type="button"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="ea-compound-grid">
          {keys.map(key => {
            const isDate = key.includes('date');
            const isDescription = key === 'description';
            return (
              <div key={key} className={isDescription ? 'ea-compound-full' : ''}>
                <label className="ea-compound-label">{labels[key]}</label>
                {isDescription ? (
                  <textarea
                    className="ea-compound-input"
                    value={item[key] || ''}
                    onChange={e => onUpdate(idx, key, e.target.value)}
                    placeholder={`Enter ${labels[key].toLowerCase()}...`}
                    rows={2}
                  />
                ) : (
                  <input
                    type={isDate ? 'date' : 'text'}
                    className="ea-compound-input"
                    value={item[key] || ''}
                    onChange={e => onUpdate(idx, key, e.target.value)}
                    placeholder={isDate ? '' : `Enter ${labels[key].toLowerCase()}...`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="ea-compound-list">
      {items.map((item, idx) => renderItemFields(item, idx))}
      <button
        className="ea-add-compound-btn"
        onClick={onAdd}
        type="button"
      >
        <i className="fas fa-plus-circle"></i>
        Add {typeLabels[type]}
      </button>
    </div>
  );
};

export default EventApply;
